import { useEffect, useMemo, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@apollo/client/react'
import { toast } from 'sonner'
import { Wizard } from '@/shared/components/wizard'
import type { WizardStepConfig } from '@/shared/components/wizard'
import { routes } from '@/shared/constants'
import { ServicesStep } from '@/features/services'
import { useRefreshAuth } from '@/features/auth'
import { useMyOrganization } from '@/features/organization/hooks/useMyOrganization'
import { usePackageCatalog } from '@/features/services/hooks/usePackageCatalog'
import { usePaymentGateways } from '@/features/payments/hooks/usePaymentGateways'
import { GatewayPicker } from '@/features/payments/components/GatewayPicker'
import {
  PaymentLinkPanel,
  type PaymentLinkData,
} from '@/features/payments/components/PaymentLinkPanel'
import {
  PortalCreateBusinessAndPaymentLinkDocument,
  PortalCreateBillingPaymentLinkDocument,
  PortalMyBusinessesDocument,
  PortalMyOrganizationDocument,
} from '@/graphql/generated/graphql'
import {
  createBusinessSchema,
  type CreateBusinessFormValues,
} from '../../schemas/createBusinessSchema'
import { useCreateBusinessFormState } from '../../hooks/useCreateBusinessFormState'
import { GeneralInfoStep } from './GeneralInfoStep'
import { AddressStep } from './AddressStep'
import { ReviewStep } from './ReviewStep'

interface AdditionalBusinessWizardProps {
  onCancel: () => void
  /**
   * Modo primera vez: el usuario aún no tiene organización. El backend creará
   * la org y el negocio en una sola operación y promoverá al usuario de Lead a
   * ORG_OWNER. En este modo se oculta la opción "misma dirección de la org" (no
   * hay org aún), se refresca el auth y se navega a la organización recién creada.
   */
  firstTime?: boolean
}

interface PaymentState {
  billingId: number
  totalReal: number
  currencyName: string
  link: PaymentLinkData | null
  linkError: string | null
}

/**
 * Wizard unificado de creación de negocio, con paso de pago (pagos-zeus-3).
 *
 * - `firstTime=false` (por defecto): crea un negocio adicional dentro de una org
 *   ya existente. - `firstTime=true`: crea org + primer negocio (el usuario
 *   recibe ORG_OWNER al crear la organización).
 *
 * En ambos modos el negocio (+ su factura) se crea JUSTO al confirmar el paso
 * de pago (`portalCreateBusinessAndPaymentLink`) y queda inactivo hasta que el
 * pago se confirme; la factura pendiente también puede pagarse después desde
 * Facturación.
 */
export function AdditionalBusinessWizard({ onCancel, firstTime = false }: AdditionalBusinessWizardProps) {
  const navigate = useNavigate()
  const persisted = useCreateBusinessFormState((s) => s.values)
  const setValues = useCreateBusinessFormState((s) => s.setValues)
  const resetStore = useCreateBusinessFormState((s) => s.reset)

  const form = useForm<CreateBusinessFormValues>({
    resolver: zodResolver(createBusinessSchema),
    defaultValues: persisted,
    mode: 'onBlur',
  })

  useEffect(() => {
    const subscription = form.watch((value) => {
      setValues(value as Partial<CreateBusinessFormValues>)
    })
    return () => subscription.unsubscribe()
  }, [form, setValues])

  // Reactivo: habilita "Siguiente" del paso Servicios sólo con ≥1 seleccionado.
  const selectedServiceIds = form.watch('packageServiceIds') ?? []
  // Si está marcada, se oculta el paso Dirección y se usa la dirección de la org.
  const useSameOrgAddress = form.watch('useSameOrgAddress')

  const { organization } = useMyOrganization()
  const { refresh: refreshAuth } = useRefreshAuth()
  const { byId } = usePackageCatalog()
  const { gateways, loading: gatewaysLoading } = usePaymentGateways()

  // Paso de pago: pasarela + moneda y resultado del alta+link.
  const [gatewayId, setGatewayId] = useState<number | null>(null)
  const [currencyId, setCurrencyId] = useState<number | null>(null)
  const [payment, setPayment] = useState<PaymentState | null>(null)
  const [retrying, setRetrying] = useState(false)

  const [doCreateAndLink, { loading }] = useMutation(
    PortalCreateBusinessAndPaymentLinkDocument,
    {
      refetchQueries: [
        { query: PortalMyOrganizationDocument },
        { query: PortalMyBusinessesDocument },
      ],
    },
  )
  const [doRetryLink] = useMutation(PortalCreateBillingPaymentLinkDocument)

  // Moneda del catálogo de los servicios elegidos (cálculo barato por render).
  let fallbackCurrency: { id: number; name: string } | null = null
  for (const id of selectedServiceIds) {
    const svc = byId.get(id)
    if (svc?.currencyId) {
      fallbackCurrency = { id: svc.currencyId, name: svc.currencyName }
      break
    }
  }

  const finish = async (paidMessage?: string) => {
    // El rol/membresía pueden haber cambiado (p. ej. recibió ORG_OWNER al crear
    // la organización, o hay un negocio nuevo): refrescar el JWT antes de
    // navegar para que guards y sidebar reaccionen.
    await refreshAuth()
    resetStore()
    form.reset()
    if (paidMessage) toast.success('¡Listo!', { description: paidMessage })
    navigate(firstTime ? routes.app.organization.detail : routes.app.businesses.list)
  }

  const handleComplete = async () => {
    if (gatewayId === null || currencyId === null) return
    const values = form.getValues()

    const orgMain =
      organization?.addresses?.find((a) => a.isMain) ?? organization?.addresses?.[0]
    const source =
      !firstTime && values.useSameOrgAddress && orgMain ? orgMain : values.address

    try {
      const { data } = await doCreateAndLink({
        variables: {
          input: {
            businessLegalName: values.legalName,
            businessLogo: values.logo || undefined,
            businessCategoryIds: values.businessCategoryIds?.length
              ? values.businessCategoryIds
              : undefined,
            plannedDayToPay: values.plannedDayToPay ?? undefined,
            licenseType: values.licenseType,
            packageServiceIds: values.packageServiceIds?.length
              ? values.packageServiceIds
              : undefined,
            addresses: [
              {
                street1: source.street1 ?? '',
                city: source.city ?? '',
                countryId: source.countryId ?? 0,
                provinceId: source.provinceId ?? 0,
                municipalityId: source.municipalityId ?? 0,
                isMain: true,
              },
            ],
            paymentGatewayId: gatewayId,
            currencyId,
          },
        },
      })
      const result = data?.portalCreateBusinessAndPaymentLink
      if (!result) return
      if (!result.billingId) {
        // Sin factura (total 0): nada que pagar.
        await finish('Tu negocio está creado.')
        return
      }
      setPayment({
        billingId: result.billingId,
        totalReal: result.totalReal,
        currencyName: result.currencyName,
        link: result.link ?? null,
        linkError: result.linkError ?? null,
      })
    } catch (err) {
      toast.error('No se pudo crear el negocio', {
        description: err instanceof Error ? err.message : undefined,
      })
    }
  }

  const handleRetryLink = async () => {
    if (!payment || retrying || gatewayId === null || currencyId === null) return
    setRetrying(true)
    try {
      const { data } = await doRetryLink({
        variables: {
          input: {
            billingId: payment.billingId,
            paymentGatewayId: gatewayId,
            currencyId,
          },
        },
      })
      const result = data?.portalCreateBillingPaymentLink
      if (result) {
        setPayment({
          billingId: result.billingId,
          totalReal: result.totalReal,
          currencyName: result.currencyName,
          link: result.link,
          linkError: null,
        })
      }
    } catch (err) {
      setPayment((prev) =>
        prev
          ? {
              ...prev,
              linkError:
                err instanceof Error
                  ? err.message
                  : 'No se pudo generar el link de pago. Intenta de nuevo.',
            }
          : prev,
      )
    } finally {
      setRetrying(false)
    }
  }

  const steps = useMemo<WizardStepConfig[]>(() => {
    const list: WizardStepConfig[] = [
      {
        id: 'general',
        label: 'Datos generales',
        render: () => <GeneralInfoStep hideOrgAddressOption={firstTime} />,
        validate: () =>
          form.trigger(['legalName', 'logo', 'licenseType', 'plannedDayToPay']),
      },
      {
        id: 'services',
        label: 'Servicios',
        render: () => <ServicesStep />,
        validate: () => form.trigger('packageServiceIds'),
        canAdvance: selectedServiceIds.length > 0,
      },
    ]
    // En modo firstTime siempre se muestra el paso Dirección (no hay org aún).
    // En modo adicional se omite si el usuario reutiliza la dirección de la org.
    if (firstTime || !useSameOrgAddress) {
      list.push({
        id: 'address',
        label: 'Dirección',
        render: () => <AddressStep />,
        validate: () => form.trigger('address'),
      })
    }
    list.push({
      id: 'review',
      label: 'Resumen',
      render: (nav) => <ReviewStep onEdit={nav.goTo} />,
    })
    list.push({
      id: 'payment',
      label: 'Pago',
      render: () => (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Tu negocio y su factura se crean al confirmar; se activan cuando el
            pago se confirme. También podrás pagar la factura más tarde desde
            Facturación.
          </p>
          <GatewayPicker
            gateways={gateways}
            loading={gatewaysLoading}
            selectedGatewayId={gatewayId}
            selectedCurrencyId={currencyId}
            fallbackCurrency={fallbackCurrency}
            onSelect={(gId, cId) => {
              setGatewayId(gId)
              setCurrencyId(cId)
            }}
            onSelectCurrency={setCurrencyId}
          />
        </div>
      ),
      canAdvance: gatewayId !== null && currencyId !== null,
    })
    return list
  }, [
    form,
    firstTime,
    selectedServiceIds.length,
    useSameOrgAddress,
    gateways,
    gatewaysLoading,
    gatewayId,
    currencyId,
    fallbackCurrency,
  ])

  // Pantalla de pago: el negocio+factura ya existen; queda pagar (o salir y
  // pagar después desde Facturación).
  if (payment) {
    return (
      <div className="space-y-5">
        <PaymentLinkPanel
          billingId={payment.billingId}
          totalReal={payment.totalReal}
          currencyName={payment.currencyName}
          link={payment.link}
          linkError={payment.linkError}
          retrying={retrying}
          onRetry={() => void handleRetryLink()}
          onPaid={() => void finish('Pago confirmado: tu negocio se está activando.')}
        />
        <p className="text-center text-xs text-muted-foreground">
          ¿Prefieres pagar más tarde?{' '}
          <button
            type="button"
            onClick={() => void finish()}
            className="font-semibold text-primary"
          >
            Salir
          </button>{' '}
          — la factura queda pendiente en Facturación.
        </p>
      </div>
    )
  }

  return (
    <FormProvider {...form}>
      <Wizard
        steps={steps}
        onComplete={handleComplete}
        onCancel={onCancel}
        submitLabel="Crear y pagar"
        loading={loading}
      />
    </FormProvider>
  )
}
